import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  qInventoryItemsList,
  qInventoryItemsCurrentStock,
  type InventoryItemRow,
  type InventoryItemStockRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { NavIcon, type ShellOutletContext } from '../../shell/shared';
import {
  FilterDropdown,
  InventoryListToolbar,
  InventoryPageShell,
  InventoryPrimaryAction,
  InventoryRow,
  InventoryRowActions,
  InventoryStatusBadge,
  InventoryTable,
  type InventoryStatusVariant,
} from './_shared';

// ─── Display view-model derived from inventory_items rows ───

interface InventoryItemVM {
  id: string;
  name: string;
  imageUrl: string | null;
  sku: string;
  category: string;
  itemType: string;
  unit: string;
  currentStock: number;
  reorderLevel: number;
  status: InventoryStatusVariant;
  // PRD.234 — Pharmacy workflow capability. Only meaningful for Products;
  // when true the item joins the Pharmacy queue and dispenses through
  // Pharmacy Confirm Dispense instead of Product Release.
  dispenseViaPharmacy: boolean;
  // PRD.260 — Packages are not physical stock; the list hides stock values.
  isPackage: boolean;
}

// PRD.191 — Current stock prefers the ledger-derived value from
// stock_movements when at least one movement exists for the item. Items
// with no movements yet fall back to inventory_items.initial_stock_quantity
// for backward compatibility (see PRD.191 §9). This fallback is temporary
// and should go away once Opening Balance backfill is implemented.
function toItemVM(row: InventoryItemRow, ledgerStock: number | undefined): InventoryItemVM {
  const isPackage = (row.item_type ?? '').toLowerCase() === 'package';
  const stock = ledgerStock ?? row.initial_stock_quantity ?? 0;
  const reorder = row.low_stock_limit ?? 10;

  // PRD.260 — Packages are not physical stock: their status is driven only by
  // the active/inactive flag, never by stock levels.
  let status: InventoryStatusVariant;
  if (row.inactive === 1 || row.status === 'Inactive') {
    status = 'inactive';
  } else if (isPackage) {
    status = 'active';
  } else if (stock <= 0) {
    status = 'out-of-stock';
  } else if (stock <= reorder) {
    status = 'low-stock';
  } else {
    status = 'active';
  }

  return {
    id: row.id,
    name: row.item_name ?? '(Unnamed)',
    imageUrl: row.image_url,
    sku: row.sku ?? '',
    category: row.category ?? '',
    itemType: row.item_type ?? '',
    unit: row.inventory_unit ?? '',
    currentStock: stock,
    reorderLevel: reorder,
    status,
    dispenseViaPharmacy: row.dispense_via_pharmacy === 1,
    isPackage,
  };
}

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'Medicine', label: 'Medicine' },
  { value: 'Clinic Supplies', label: 'Clinic Supplies' },
  { value: 'Consumables', label: 'Consumables' },
  { value: 'Injectables', label: 'Injectables' },
  { value: 'Skin Care Product', label: 'Skin Care Product' },
  { value: 'Equipment', label: 'Equipment' },
] as const;

const STOCK_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'in-stock', label: 'In Stock' },
  { value: 'low-stock', label: 'Low Stock' },
  { value: 'out-of-stock', label: 'Out of Stock' },
] as const;

const SORT_OPTIONS = [
  { value: 'last-updated', label: 'Last updated' },
  { value: 'name-az', label: 'Name A-Z' },
  { value: 'stock-low', label: 'Stock low to high' },
] as const;

const COLUMNS = [
  { label: 'Item', width: '2fr' },
  { label: 'SKU', width: '1fr' },
  { label: 'Category', width: '1fr' },
  { label: 'Stock', width: '120px', align: 'right' as const },
  { label: 'Reorder', width: '90px', align: 'right' as const },
  { label: 'Status', width: '130px' },
  { label: 'Actions', width: '90px', align: 'right' as const },
];

// ─── Page ───

export function InventoryItemsPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const navigate = useNavigate();

  const { db } = usePowerSyncDb();
  const { data: rows = [] } = usePowerSyncQuery<InventoryItemRow>(qInventoryItemsList());
  const { data: stockRows = [] } = usePowerSyncQuery<InventoryItemStockRow>(
    qInventoryItemsCurrentStock(),
  );

  // ── DEBUG (PRD.187 sync diagnosis) ─────────────────────────────────
  // Logs every time the local SQLite query result changes, plus a raw
  // count via db.getAll so we can compare PowerSync hook output against
  // what's actually in the local mirror. Remove once the inventory list
  // is confirmed populating end-to-end.
  useEffect(() => {
    console.log('[InventoryItems] usePowerSyncQuery rows:', rows.length, rows);
    console.log('[InventoryItems] usePowerSyncQuery stockRows:', stockRows.length, stockRows);
    if (!db) {
      console.log('[InventoryItems] db not ready');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const direct = await db.getAll<InventoryItemRow>(
          'SELECT * FROM inventory_items ORDER BY item_name ASC',
        );
        if (cancelled) return;
        console.log('[InventoryItems] db.getAll direct count:', direct.length, direct);
        const status = await db.getAll<{ name: string }>(
          "SELECT name FROM sqlite_master WHERE type='table' AND name LIKE 'inventory%'",
        );
        if (cancelled) return;
        console.log('[InventoryItems] sqlite tables (inventory*):', status);
      } catch (err) {
        if (!cancelled) console.error('[InventoryItems] db.getAll error:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [db, rows, stockRows]);
  // ── /DEBUG ─────────────────────────────────────────────────────────
  const stockMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of stockRows) map.set(r.inventory_item_id, r.current_stock ?? 0);
    return map;
  }, [stockRows]);
  const items = useMemo(() => rows.map((r) => toItemVM(r, stockMap.get(r.id))), [rows, stockMap]);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] =
    useState<(typeof CATEGORY_OPTIONS)[number]['value']>('all');
  const [stockFilter, setStockFilter] = useState<(typeof STOCK_OPTIONS)[number]['value']>('all');
  const [sortBy, setSortBy] = useState<(typeof SORT_OPTIONS)[number]['value']>('last-updated');

  const filtered = useMemo(() => {
    let result = items;
    if (categoryFilter !== 'all') {
      result = result.filter((i) => i.category === categoryFilter);
    }
    if (stockFilter !== 'all') {
      result = result.filter((i) => {
        if (stockFilter === 'out-of-stock') return i.status === 'out-of-stock';
        if (stockFilter === 'low-stock') return i.status === 'low-stock';
        return i.status === 'active';
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          i.sku.toLowerCase().includes(q) ||
          i.category.toLowerCase().includes(q),
      );
    }
    if (sortBy === 'name-az') {
      result = [...result].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'stock-low') {
      result = [...result].sort((a, b) => a.currentStock - b.currentStock);
    }
    return result;
  }, [items, search, categoryFilter, stockFilter, sortBy]);

  const handleAdd = () => {
    navigate('/inventory/items/new');
  };

  const handleEdit = (item: InventoryItemVM) => {
    // Every item type opens the full create/edit page, which hydrates and
    // updates medicine / product / supply / service / package alike.
    navigate(`/inventory/items/${item.id}/edit`);
  };

  return (
    <InventoryPageShell
      title="Inventory Items"
      subtitle="Master list of clinic stock items"
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={<InventoryPrimaryAction label="Add Item" onClick={handleAdd} />}
    >
      <InventoryListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search item name, SKU, category..."
        filters={
          <>
            <FilterDropdown
              label="Category"
              value={categoryFilter}
              options={CATEGORY_OPTIONS}
              onChange={setCategoryFilter}
            />
            <FilterDropdown
              label="Stock"
              value={stockFilter}
              options={STOCK_OPTIONS}
              onChange={setStockFilter}
            />
            <FilterDropdown
              label="Sort"
              value={sortBy}
              options={SORT_OPTIONS}
              onChange={setSortBy}
            />
          </>
        }
      />

      <div style={{ height: 'var(--sp-4)', flexShrink: 0 }} />

      <InventoryTable
        columns={COLUMNS}
        empty={
          filtered.length === 0
            ? {
                show: true,
                title: 'No inventory items yet.',
                hint: 'Add your first item to start tracking clinic stock.',
              }
            : undefined
        }
      >
        {filtered.map((item) => (
          <InventoryRow key={item.id} columns={COLUMNS} clickable onClick={() => handleEdit(item)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', minWidth: 0 }}>
              <ItemThumb url={item.imageUrl} />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                    lineHeight: 'var(--line-height-base)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.name}
                </div>
                {/* PRD.205 — Type badge so Medicine vs Product is unambiguous
                    in the list. Underlying DB stores lowercase ('medicine' /
                    'product'); we present it with title case + tone. */}
                <ItemTypeBadge type={item.itemType} />
              </div>
            </div>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
              {item.sku}
            </span>
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
              {item.category}
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: item.isPackage ? 'var(--text-muted)' : 'var(--text)',
                textAlign: 'right',
              }}
            >
              {/* PRD.260 — Packages are not stock-tracked; show a dash so the
                  list never implies a physical balance. */}
              {item.isPackage ? '—' : `${item.currentStock} ${item.unit}`}
            </span>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-muted)',
                textAlign: 'right',
              }}
            >
              {item.isPackage ? '—' : item.reorderLevel}
            </span>
            <InventoryStatusBadge status={item.status} />
            <InventoryRowActions onEdit={() => handleEdit(item)} />
          </InventoryRow>
        ))}
      </InventoryTable>
    </InventoryPageShell>
  );
}

// Thumbnail for the item's product image, with a camera-icon fallback when
// no image has been uploaded.
function ItemThumb({ url }: { url: string | null }) {
  return (
    <div
      style={{
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #f3f4f6)',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-muted)',
      }}
    >
      {url ? (
        <img
          src={url}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <NavIcon name="camera" />
      )}
    </div>
  );
}

// PRD.205 — small badge for inventory item type. Medicine = green, Product
// = indigo, anything else = neutral. Falls back to the raw string in
// title-case if a future type rolls in.
function ItemTypeBadge({ type }: { type: string }) {
  const t = (type ?? '').toLowerCase();
  const palette =
    t === 'medicine'
      ? { bg: 'var(--color-success-bg)', color: 'var(--color-success-text)' }
      : t === 'product'
        ? { bg: 'var(--color-info-bg, #dbeafe)', color: 'var(--color-info-text, #1e40af)' }
        : // PRD.260 — Package badge tone (violet).
          t === 'package'
          ? { bg: 'var(--color-accent-bg, #ede9fe)', color: 'var(--color-accent-text, #6d28d9)' }
          : // PRD.264 — Supply badge tone (teal).
            t === 'supply'
            ? { bg: '#ecfeff', color: '#0e7490' }
            : // PRD.264 follow-up — Service badge tone (green).
              t === 'service'
              ? { bg: '#f0fdf4', color: '#15803d' }
              : // PRD.266 — Vaccination (amber) and Injection (rose).
                t === 'vaccination'
                ? { bg: '#fffbeb', color: '#b45309' }
                : t === 'injection'
                  ? { bg: '#fff1f2', color: '#be123c' }
                  : { bg: 'var(--bg-surface-2)', color: 'var(--text-muted)' };
  const label = t ? t.charAt(0).toUpperCase() + t.slice(1) : '—';
  return (
    <span
      style={{
        display: 'inline-block',
        marginTop: 2,
        padding: '1px 8px',
        borderRadius: 'var(--radius-full)',
        background: palette.bg,
        color: palette.color,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </span>
  );
}
